import { loyaltyApi } from 'api'
import { Offer } from 'api/types'
import { Customer, order as squareOrderApi } from '@squareup/sq-addons-web-sdk'
import { useCallback, useEffect } from 'react'
import { buildTranslate } from 'locales'

const t = buildTranslate('general')

type CustomerLoyaltyInfo = {
  pointBalance: number
  availableOffers: Offer[]
  unavailableOffers: Offer[]
  pointsCurrencySingular: string
  pointsCurrencyPlural: string
  isErrorFetchingOffers: boolean
  isFetchingOffers: boolean
  fetchOffers: () => Promise<void>
}

const emptyOffers: Offer[] = []
export function useCustomerLoyaltyInfo(
  customer: Customer | null
): CustomerLoyaltyInfo {
  const customerReferenceId = customer?.referenceId
  const [getOffers, { data, error, isLoading, isUninitialized }] =
    loyaltyApi().useGetOffersMutation()

  const fetchOffers = useCallback(async () => {
    const order = await squareOrderApi.retrieve()

    if (customerReferenceId && order) {
      getOffers({
        userIdentifier: customerReferenceId,
        order,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerReferenceId])

  useEffect(() => {
    fetchOffers()
  }, [fetchOffers])

  const pointBalance = data?.points_balance ?? 0
  const availableOffers = data?.available_offers ?? emptyOffers
  const unavailableOffers = data?.unavailable_offers ?? emptyOffers
  const pointsCurrencySingular =
    data?.points_currency ?? t('pointsCurrencySingular')
  const pointsCurrencyPlural =
    data?.points_currency_plural ?? t('pointsCurrencyPlural')

  return {
    pointBalance,
    availableOffers,
    unavailableOffers,
    isErrorFetchingOffers: !!error,
    isFetchingOffers: isLoading || isUninitialized,
    fetchOffers,
    pointsCurrencySingular,
    pointsCurrencyPlural,
  }
}
